import { Box, Stack } from '@mui/material';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { ProductGroupCount, ProductGroup } from '@retail/products/types';
import { useMemo, useState } from 'react';
import { ProductGroupsTreeViewMultiselect } from '../ProductGroupsTree';
import { SearchInputField } from '../Search';
import { CollapsibleFilterField } from './CollapsibleFilterField';
import { productGroupTreeHeight, productGroupTreeScrollSx } from './productGroupTreeScrollSx';

interface Props {
  productGroups: ProductGroupCount[];
  selectedProductGroups: string[];
  defaultClosed?: boolean;
  /** Max height of the scrollable tree when the filter is open. */
  treeHeight?: number;
  selectProductGroup: (productGroup: string) => void;
  getCount?: (productGroup: ProductGroup) => number | undefined;
}

const matchesSearch = (
  { name, groupNumber }: Pick<ProductGroup, 'name' | 'groupNumber'>,
  lowerCasedSearch: string
) => name.toLowerCase().startsWith(lowerCasedSearch) || groupNumber.startsWith(lowerCasedSearch);

const filterProductGroupTree = (productGroups: ProductGroupCount[], search: string) => {
  if (!search) return productGroups;
  const lowerCasedSearch = search.toLowerCase();

  return productGroups
    .map((overGroup) => ({
      ...overGroup,
      subGroups: (overGroup.subGroups ?? [])
        .map((mainGroup) => ({
          ...mainGroup,
          subGroups: (mainGroup.subGroups ?? []).filter((productGroup) =>
            matchesSearch(productGroup, lowerCasedSearch)
          ),
        }))
        .filter(
          (mainGroup) =>
            mainGroup.subGroups.length > 0 || matchesSearch(mainGroup, lowerCasedSearch)
        ),
    }))
    .filter(
      (overGroup) => overGroup.subGroups.length > 0 || matchesSearch(overGroup, lowerCasedSearch)
    );
};

export function ProductGroupMultiselectFilter({
  productGroups,
  selectedProductGroups,
  defaultClosed,
  treeHeight = productGroupTreeHeight,
  selectProductGroup,
  getCount,
}: Props) {
  const t = useMyAssortmentTFunction();
  const [productGroupSearch, setProductGroupSearch] = useState('');

  const filteredProductGroups = useMemo(
    () => filterProductGroupTree(productGroups, productGroupSearch),
    [productGroupSearch, productGroups]
  );

  return (
    <CollapsibleFilterField
      label={t('myAssortment.filters.productGroup')}
      defaultOpen={!defaultClosed}
      gap={0}
    >
      <Stack gap={0.5}>
        <SearchInputField
          placeholder={t('myAssortment.filters.productGroupSearch')}
          value={productGroupSearch}
          onChange={(e) => setProductGroupSearch(e.target.value)}
          onReset={() => setProductGroupSearch('')}
        />

        <Box sx={productGroupTreeScrollSx(treeHeight)}>
          <ProductGroupsTreeViewMultiselect
            productGroups={filteredProductGroups}
            getCount={getCount}
            selectedProductGroups={selectedProductGroups}
            selectProductGroup={selectProductGroup}
          />
        </Box>
      </Stack>
    </CollapsibleFilterField>
  );
}
